import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';

import Landing from './pages/Landing';
import Login from './pages/Login';
import CharacterSelect from './pages/CharacterSelect';
import Shop from './pages/Shop';
import Orders from './pages/Orders';
import './index.css';

// ==================== AUTH CONTEXT ====================
export const AuthContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }

function AuthProvider({ children }) {
  const [igLogin, setIgLogin] = useState(false);
  const [token, setToken] = useState(() => {
    // Priorité : ?token= dans l'URL sur /shop ou / (auth IG via FiveM)
    // Ne pas intercepter sur /select (token temporaire Discord)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');
    const isSelectPage = window.location.pathname === '/select';
    if (urlToken && !isSelectPage) {
      localStorage.setItem('fastbox_token', urlToken);
      window.history.replaceState({}, '', window.location.pathname);
      // Marquer comme connexion IG pour redirection auto vers /shop
      setTimeout(() => setIgLogin(true), 0);
      return urlToken;
    }
    return localStorage.getItem('fastbox_token');
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async (t) => {
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const res = await axios.get('/api/auth/me', { headers: { Authorization: `Bearer ${t}` } });
      setUser(res.data);
    } catch (_) {
      localStorage.removeItem('fastbox_token');
      setToken(null);
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUser(token); }, [token, fetchUser]);

  const login = (newToken) => {
    localStorage.setItem('fastbox_token', newToken);
    setToken(newToken);
    fetchUser(newToken);
  };
  const logout = () => {
    localStorage.removeItem('fastbox_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ token, user, loading, login, logout, igLogin }}>
      {children}
    </AuthContext.Provider>
  );
}

// ==================== GUARDS ====================
// Redirige automatiquement vers /shop lors d'une auth IG (token dans l'URL)
function IgShopRedirect() {
  const { igLogin, token, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!loading && igLogin && token) {
      navigate('/shop', { replace: true });
    }
  }, [igLogin, token, loading, navigate]);
  return null;
}

function RequireAuth({ children }) {
  const { token, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 12, color: 'var(--text-muted)' }}>
      <i className="fa-solid fa-spinner spinner" style={{ fontSize: 24, color: 'var(--primary)' }} />
      <span>Chargement...</span>
    </div>
  );
  return token ? children : <Navigate to="/" replace />;
}

// ==================== APP ====================
export default function App() {
  return (
    <AuthProvider>
      <Router>
        <IgShopRedirect />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/select" element={<CharacterSelect />} />
          <Route path="/shop" element={<RequireAuth><Shop /></RequireAuth>} />
          <Route path="/orders" element={<RequireAuth><Orders /></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}
